import { useState, useEffect, useCallback, Fragment } from 'react';
import api from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, Save, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';

const SG_STATUS_ICON = {
  COMPLETED: CheckCircle2,
  OVERDUE: AlertTriangle,
};

export default function PCCMProgressEntry({ projectId }) {
  const [entries, setEntries] = useState([]);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filterSystem, setFilterSystem] = useState('');
  const [pendingUpdates, setPendingUpdates] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterSystem ? `?system_code=${filterSystem}&limit=500` : '?limit=500';
      const [entriesRes, sysRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/pccm/entries${params}`),
        api.get(`/v1/projects/${projectId}/pccm/systems`),
      ]);
      setEntries(entriesRes.data?.entries || []);
      setSystems(sysRes.data || []);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  }, [projectId, filterSystem]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDateChange = (entryId, sgId, field, value) => {
    const key = `${entryId}|${sgId}`;
    setPendingUpdates(prev => ({
      ...prev,
      [key]: { ...prev[key], entryId, sgId, [field]: value || null },
    }));
  };

  const saveAll = async () => {
    const updates = Object.values(pendingUpdates);
    if (updates.length === 0) { toast.info('No changes to save'); return; }
    setSaving(true);
    let ok = 0, fail = 0;
    for (const u of updates) {
      try {
        const body = {};
        if (u.plannedDate !== undefined) body.plannedDate = u.plannedDate;
        if (u.forecastDate !== undefined) body.forecastDate = u.forecastDate;
        if (u.actualDate !== undefined) body.actualDate = u.actualDate;
        await api.put(`/v1/projects/${projectId}/pccm/entries/${u.entryId}/stage-gates/${u.sgId}`, body);
        ok++;
      } catch { fail++; }
    }
    setPendingUpdates({});
    toast.success(`Saved ${ok} updates${fail ? `, ${fail} failed` : ''}`);
    fetchData();
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;

  const pendingCount = Object.keys(pendingUpdates).length;

  return (
    <div className="space-y-4" data-testid="pccm-progress-entry">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <select value={filterSystem} onChange={e => setFilterSystem(e.target.value)} data-testid="progress-filter-system"
            className="px-2 py-1.5 text-xs rounded-lg border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <option value="">All Systems</option>
            {systems.map(s => <option key={s.systemCode} value={s.systemCode}>{s.systemCode} - {s.systemName}</option>)}
          </select>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{entries.length} items</span>
        </div>
        <Button size="sm" onClick={saveAll} disabled={saving || pendingCount === 0} data-testid="progress-save-all-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          Save All {pendingCount > 0 && `(${pendingCount})`}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Total Items</p>
          <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{entries.length}</p>
        </div>
        <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Completed</p>
          <p className="text-xl font-bold" style={{ color: '#22c55e' }}>{entries.filter(e => e.overallStatus === 'COMPLETED').length}</p>
        </div>
        <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Overdue</p>
          <p className="text-xl font-bold" style={{ color: '#ef4444' }}>{entries.filter(e => e.overallStatus === 'DELAYED').length}</p>
        </div>
        <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Pending Changes</p>
          <p className="text-xl font-bold" style={{ color: '#f59e0b' }}>{pendingCount}</p>
        </div>
      </div>

      {/* Progress Grid */}
      <div className="rounded-lg border overflow-x-auto" style={{ borderColor: 'var(--border-color)' }}>
        <table className="w-full text-xs">
          <thead>
            <tr style={{ background: 'var(--card-bg-alt)' }}>
              <th className="p-2 text-left font-medium sticky left-0 z-10" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)', minWidth: 120 }}>Tag</th>
              <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)', minWidth: 150 }}>Description</th>
              {entries[0]?.stageGateProgress?.map(sg => (
                <th key={sg.stageGateId} className="p-2 text-center font-medium" colSpan={3} style={{ color: 'var(--text-muted)', borderLeft: '2px solid var(--border-color)' }}>
                  {sg.stageGateName}
                </th>
              ))}
            </tr>
            <tr style={{ background: 'var(--card-bg-alt)' }}>
              <th className="p-1 sticky left-0 z-10" style={{ background: 'var(--card-bg-alt)' }} />
              <th className="p-1" />
              {entries[0]?.stageGateProgress?.map(sg => (
                <Fragment key={`${sg.stageGateId}-sub`}>
                  <th className="p-1 text-center text-[10px]" style={{ color: '#3b82f6', borderLeft: '2px solid var(--border-color)' }}>Plan</th>
                  <th className="p-1 text-center text-[10px]" style={{ color: '#f59e0b' }}>Fcst</th>
                  <th className="p-1 text-center text-[10px]" style={{ color: '#10b981' }}>Actual</th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.map(entry => (
              <tr key={entry.id} style={{ borderTop: '1px solid var(--border-color)', background: 'var(--card-bg)' }}>
                <td className="p-2 font-mono font-semibold sticky left-0 z-10" style={{ color: 'var(--accent-primary)', background: 'var(--card-bg)' }}>
                  {entry.tagNumber}
                </td>
                <td className="p-2 truncate max-w-[150px]" style={{ color: 'var(--text-secondary)' }}>{entry.itemDescription}</td>
                {(entry.stageGateProgress || []).map(sg => {
                  const key = `${entry.id}|${sg.stageGateId}`;
                  const pending = pendingUpdates[key] || {};
                  return (
                    <Fragment key={sg.stageGateId}>
                      <td className="p-1" style={{ borderLeft: '2px solid var(--border-color)' }}>
                        <input type="date" value={pending.plannedDate !== undefined ? (pending.plannedDate || '') : (sg.plannedDate?.slice(0, 10) || '')}
                          onChange={e => handleDateChange(entry.id, sg.stageGateId, 'plannedDate', e.target.value)}
                          className="px-1 py-0.5 text-[10px] rounded border w-[105px]"
                          style={{ background: 'var(--input-bg)', borderColor: pending.plannedDate !== undefined ? '#3b82f6' : 'var(--border-color)', color: 'var(--text-primary)' }} />
                      </td>
                      <td className="p-1">
                        <input type="date" value={pending.forecastDate !== undefined ? (pending.forecastDate || '') : (sg.forecastDate?.slice(0, 10) || '')}
                          onChange={e => handleDateChange(entry.id, sg.stageGateId, 'forecastDate', e.target.value)}
                          className="px-1 py-0.5 text-[10px] rounded border w-[105px]"
                          style={{ background: 'var(--input-bg)', borderColor: pending.forecastDate !== undefined ? '#f59e0b' : 'var(--border-color)', color: 'var(--text-primary)' }} />
                      </td>
                      <td className="p-1">
                        <input type="date" value={pending.actualDate !== undefined ? (pending.actualDate || '') : (sg.actualDate?.slice(0, 10) || '')}
                          onChange={e => handleDateChange(entry.id, sg.stageGateId, 'actualDate', e.target.value)}
                          className="px-1 py-0.5 text-[10px] rounded border w-[105px]"
                          style={{ background: 'var(--input-bg)', borderColor: pending.actualDate !== undefined ? '#10b981' : 'var(--border-color)', color: 'var(--text-primary)' }} />
                      </td>
                    </Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}



